import { Info, TrendingUp, TrendingDown } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useGetBrandAbTestStats } from "@/resources/ab-test/ab-test.api";

interface KPICardProps {
  label: string;
  value: string;
  change: number;
  changeLabel: string;
  tooltip: string;
  isLoading?: boolean;
}

function KPICard({ label, value, change, changeLabel, tooltip, isLoading }: KPICardProps) {
  const isPositive = change > 0;
  const TrendIcon = isPositive ? TrendingUp : TrendingDown;

  return (
    <Card className="flex-1 min-w-0">
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <p className="text-sm text-muted-foreground">{label}</p>
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger className="cursor-help" onClick={(e) => e.preventDefault()}>
                <Info className="h-3.5 w-3.5 text-muted-foreground" />
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-[200px]">
                <p>{tooltip}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        {isLoading ? (
          <div className="h-8 w-24 rounded bg-muted animate-pulse mb-3" />
        ) : (
          <p className="text-2xl font-semibold text-foreground tracking-tight">{value}</p>
        )}
        {change !== 0 && !isLoading && (
          <div className="flex items-center gap-1.5 mt-3">
            <Badge
              className={`border-0 gap-1 font-medium ${
                isPositive
                  ? "bg-status-success-soft text-foreground"
                  : "bg-status-error-soft text-foreground"
              }`}
            >
              <TrendIcon className="h-3 w-3" />
              {isPositive ? "+" : ""}
              {change}%
            </Badge>
            <span className="text-xs text-muted-foreground">{changeLabel}</span>
          </div>
        )}
        {change === 0 && !isLoading && (
          <div className="mt-3 h-5" />
        )}
      </CardContent>
    </Card>
  );
}

interface ABTestKPIsProps {
  brandId: number | null | undefined;
}

export function ABTestKPIs({ brandId }: ABTestKPIsProps) {
  const { data: stats, isPending } = useGetBrandAbTestStats(brandId);

  const cards: KPICardProps[] = [
    {
      label: "Incremental Sales",
      value: stats ? `+$${stats.totalSalesLift.toLocaleString()}` : "—",
      change: stats?.salesLiftChange ?? 0,
      changeLabel: "vs last month",
      tooltip: "Total incremental sales from decisive A/B tests (sum of absolute sales differences)",
    },
    {
      label: "Annualized Sales",
      value: stats ? `$${stats.annualizedSales.toLocaleString()}` : "—",
      change: stats?.annualizedSalesChange ?? 0,
      changeLabel: "vs last year",
      tooltip: "Projected annual incremental sales based on a 6-week testing cycle (8.7× annualization factor)",
    },
    {
      label: "Total CVR Lift",
      value: stats ? `+${stats.totalCvrLift.toFixed(1)}%` : "—",
      change: stats?.cvrLiftChange ?? 0,
      changeLabel: "vs last month",
      tooltip: "Total conversion rate lift from decisive A/B tests",
    },
    {
      label: "Tests Running",
      value: stats ? `${stats.runningTests} of ${stats.totalTests}` : "—",
      change: stats?.runningTestsChange ?? 0,
      changeLabel: "vs last month",
      tooltip: "Number of A/B tests currently running out of total tests",
    },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {cards.map((card) => (
        <KPICard key={card.label} {...card} isLoading={isPending} />
      ))}
    </div>
  );
}
